import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Mail, KeyRound, CheckCircle, Eye, EyeOff, Copy, Check, Monitor, Send } from "lucide-react";
import api from "../lib/api";

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: email, 2: choose method, 3: new password
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const navigate = useNavigate();

  const handleRequestToken = async (sendEmail = false) => {
    setLoading(true);

    try {
      const response = await api.post("/auth/forgot-password", { 
        email,
        send_email: sendEmail 
      });
      
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        setEmailSent(response.data.email_sent);
        setEmailError(response.data.email_error);
        setStep(3);
        
        if (sendEmail && response.data.email_sent) {
          toast.success("Código enviado para seu e-mail!");
        } else if (sendEmail && !response.data.email_sent) {
          toast.warning(`Não foi possível enviar e-mail: ${response.data.email_error}. Código exibido na tela.`);
        } else {
          toast.success("Código gerado com sucesso!");
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("E-mail não encontrado no sistema.");
      } else {
        toast.error(error.response?.data?.detail || "Erro ao solicitar recuperação");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Primeiro verificar se o e-mail existe
      const response = await api.post("/auth/forgot-password", { 
        email,
        send_email: false 
      });
      
      if (response.data.reset_token) {
        // Salvar o token temporariamente e ir para escolha do método
        setResetToken(response.data.reset_token);
        setStep(2);
        toast.success("E-mail encontrado! Escolha como deseja receber o código.");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("E-mail não encontrado no sistema.");
      } else {
        toast.error(error.response?.data?.detail || "Erro ao verificar e-mail");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChooseMethod = async (method) => {
    if (method === "screen") {
      // Já temos o token, só mostrar
      setStep(3);
      toast.success("Código exibido na tela!");
    } else {
      // Tentar enviar por e-mail
      setLoading(true);
      try {
        const response = await api.post("/auth/forgot-password", { 
          email,
          send_email: true 
        });
        
        setResetToken(response.data.reset_token);
        setEmailSent(response.data.email_sent);
        setEmailError(response.data.email_error);
        setStep(3);
        
        if (response.data.email_sent) {
          toast.success("Código enviado para seu e-mail!");
        } else {
          toast.warning(`Não foi possível enviar e-mail. Código exibido na tela.`);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Erro ao enviar e-mail");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(resetToken);
    setCopied(true);
    toast.success("Código copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setLoading(true);

    try {
      await api.post("/auth/reset-password", {
        email,
        reset_token: resetToken,
        new_password: newPassword,
      });
      toast.success("Senha alterada com sucesso!");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao redefinir senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            EstoqueClinic
          </h1>
          <p className="text-slate-500">
            Recuperação de Senha
          </p>
        </div>

        {/* Step 1: Digite o e-mail */}
        {step === 1 && (
          <Card className="border-slate-200 shadow-soft">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-heading flex items-center gap-2">
                <KeyRound className="h-6 w-6 text-primary" />
                Esqueci a Senha
              </CardTitle>
              <CardDescription>
                Digite seu e-mail cadastrado para recuperar sua senha
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleVerifyEmail}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail cadastrado</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                    data-testid="forgot-email-input"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <>
                      <Mail className="mr-2 h-5 w-5" />
                      Continuar
                    </>
                  )}
                </Button>
                <Link
                  to="/login"
                  className="flex items-center justify-center text-sm text-slate-500 hover:text-primary"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Voltar para o login
                </Link>
              </CardFooter>
            </form>
          </Card>
        )}

        {/* Step 2: Escolher método de recuperação */}
        {step === 2 && (
          <Card className="border-slate-200 shadow-soft">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-heading flex items-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
                E-mail Encontrado
              </CardTitle>
              <CardDescription>
                Como você deseja receber o código de recuperação?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>E-mail:</strong> {email}
                </p>
              </div>

              <div className="grid gap-3">
                <Button
                  onClick={() => handleChooseMethod("screen")}
                  variant="outline"
                  className="w-full h-14 flex items-center justify-start gap-3 text-left"
                  disabled={loading}
                  data-testid="choose-screen-btn"
                >
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Monitor className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Ver código na tela</p>
                    <p className="text-xs text-slate-500">Mostrar o código agora (recomendado)</p>
                  </div>
                </Button>

                <Button
                  onClick={() => handleChooseMethod("email")}
                  variant="outline"
                  className="w-full h-14 flex items-center justify-start gap-3 text-left"
                  disabled={loading}
                  data-testid="choose-email-btn"
                >
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Send className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Enviar por e-mail</p>
                    <p className="text-xs text-slate-500">Receber o código no seu e-mail</p>
                  </div>
                </Button>
              </div>
            </CardContent>
            <CardFooter>
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setResetToken("");
                }}
                className="flex items-center justify-center w-full text-sm text-slate-500 hover:text-primary"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Voltar
              </button>
            </CardFooter>
          </Card>
        )}

        {/* Step 3: Mostrar código e definir nova senha */}
        {step === 3 && (
          <Card className="border-slate-200 shadow-soft">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-heading flex items-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
                Definir Nova Senha
              </CardTitle>
              <CardDescription>
                Usuário encontrado: <strong>{email}</strong>
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleResetPassword}>
              <CardContent className="space-y-4">
                {/* Mostrar código de recuperação */}
                <div className={`p-3 rounded-lg border ${emailSent ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                  <p className={`text-xs mb-2 ${emailSent ? 'text-blue-700' : 'text-green-700'}`}>
                    <strong>
                      {emailSent 
                        ? '✓ Código enviado para seu e-mail e exibido abaixo:' 
                        : '✓ Código de recuperação gerado:'}
                    </strong>
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono tracking-wider text-center" data-testid="reset-token-display">
                      {resetToken}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCopyToken}
                      data-testid="copy-token-btn"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className={`text-xs mt-2 ${emailSent ? 'text-blue-600' : 'text-green-600'}`}>
                    Guarde este código caso precise recuperar novamente. Expira em 1 hora.
                  </p>
                </div>

                {emailError && !emailSent && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700">
                      <strong>Aviso:</strong> {emailError}. O código está disponível acima.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="h-11 pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Mínimo de 6 caracteres</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-11"
                    data-testid="confirm-password-input"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    "Redefinir Senha"
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setResetToken("");
                    setEmailSent(false);
                    setEmailError(null);
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                  className="flex items-center justify-center text-sm text-slate-500 hover:text-primary"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Começar novamente
                </button>
              </CardFooter>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
